
import React, { useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";

const PAGE_SIZE = 5;

const LiveBidFeed = ({
  liveBids = [],
  liveBidRanking = [],
  isConnected = false,
  auctionStatus = "active",
  userRole = "buyer",
  currentUserCode = "",
  displayRank = "showToAll",
  vendorMasking = false,
  onlineSuppliers = [],
  invitedSuppliers = [],
  itemLeaderboard = [],
  auctionId = "",
  authToken = "",
  serverRemainingSeconds = null,
  firstQuote = false,
  firstQuoteBy = "Supplier",
  supplierInitialPrices = {},
  supplierList = [],
  startPrice = false,
  itemPrices = {},
  itemReservePrice = {},
}) => {
  const isBuyer = userRole === "buyer";

  const maskedNameMap = React.useMemo(() => {
    if (!vendorMasking || !isBuyer) return {};
    const map = {};
    liveBidRanking.forEach((row, idx) => {
      if (row.supplierCode) map[row.supplierCode] = `Supplier ${idx + 1}`;
    });
    let counter = liveBidRanking.length + 1;
    liveBids.forEach((bid) => {
      if (bid.supplierCode && !map[bid.supplierCode]) {
        map[bid.supplierCode] = `Supplier ${counter++}`;
      }
    });
    return map;
  }, [vendorMasking, isBuyer, liveBidRanking, liveBids]);

  const getMaskedName = (supplierCode, supplierName) => {
    if (vendorMasking && isBuyer && auctionStatus !== "ended") {
      return maskedNameMap[supplierCode] || "Supplier ?";
    }
    return supplierName || supplierCode || "--";
  };

  const getSupplierName = (supplierCode) => {
    const found = supplierList.find(
      (s) =>
        String(s.AssociateNumber) === String(supplierCode) ||
        String(s.userId) === String(supplierCode)
    );
    return found?.organisationName || found?.name || supplierCode || "--";
  };

  const fqInitialLeaderboard = React.useMemo(() => {
    if (!firstQuote || firstQuoteBy !== "Buyer") return [];
    if (!supplierInitialPrices || Object.keys(supplierInitialPrices).length === 0) return [];
    const rows = Object.entries(supplierInitialPrices).map(([supplierId, itemPricesMap]) => {
      const total = Object.values(itemPricesMap || {}).reduce(
        (sum, v) => sum + (parseFloat(v) || 0), 0
      );
      return {
        supplierCode: supplierId,
        supplierName: getSupplierName(supplierId),
        totalBid: total,
        isInitialPrice: true,
        lastBidTime: null,
      };
    });
    rows.sort((a, b) => a.totalBid - b.totalBid);
    return rows.map((r, idx) => ({ ...r, rank: idx + 1 }));
  }, [firstQuote, firstQuoteBy, supplierInitialPrices, supplierList]); // eslint-disable-line

  const fqInitialItemLeaderboard = React.useMemo(() => {
    if (!firstQuote || firstQuoteBy !== "Buyer") return [];
    if (!supplierInitialPrices || Object.keys(supplierInitialPrices).length === 0) return [];
    const itemKeySet = new Set();
    Object.values(supplierInitialPrices).forEach((itemPricesMap) => {
      Object.keys(itemPricesMap || {}).forEach((k) => itemKeySet.add(k));
    });
    return Array.from(itemKeySet).map((itemKey) => {
      const suppliers = [];
      Object.entries(supplierInitialPrices).forEach(([supplierId, itemPricesMap]) => {
        const price = parseFloat(itemPricesMap?.[itemKey]);
        if (!isNaN(price)) {
          suppliers.push({
            supplierCode: supplierId,
            supplierName: getSupplierName(supplierId),
            bidAmount: price,
            bidTime: null,
            isInitialPrice: true,
          });
        }
      });
      suppliers.sort((a, b) => a.bidAmount - b.bidAmount);
      const ranked = suppliers.map((s, idx) => ({ ...s, rank: idx + 1 }));
      return {
        itemKey,
        local_id: itemKey,
        itemCode: itemKey,
        itemDesc: "",
        lotNumber: "",
        rankings: ranked,
        winner: ranked[0] || null,
      };
    });
  }, [firstQuote, firstQuoteBy, supplierInitialPrices, supplierList]); // eslint-disable-line

  const effectiveLeaderboard = React.useMemo(() => {
    if (liveBidRanking.length > 0) return liveBidRanking;
    if (firstQuote && firstQuoteBy === "Buyer" && fqInitialLeaderboard.length > 0)
      return fqInitialLeaderboard;
    return liveBidRanking;
  }, [liveBidRanking, firstQuote, firstQuoteBy, fqInitialLeaderboard]);

  const effectiveItemLeaderboard = React.useMemo(() => {
    if (itemLeaderboard.length > 0) return itemLeaderboard;
    if (firstQuote && firstQuoteBy === "Buyer" && fqInitialItemLeaderboard.length > 0)
      return fqInitialItemLeaderboard;
    return itemLeaderboard;
  }, [itemLeaderboard, firstQuote, firstQuoteBy, fqInitialItemLeaderboard]);

  const [bidHistoryModal, setBidHistoryModal] = useState(null);

  const openBidHistory = (item, supplierRow) => {
    const itemKey = item?.local_id ?? item?.itemKey ?? item?.itemCode ?? "";
    const supBids = liveBids
      .filter(b => String(b.supplierCode) === String(supplierRow.supplierCode))
      .sort((a, b) => new Date(b.bidTime || 0) - new Date(a.bidTime || 0));
    const displayName = getMaskedName(supplierRow.supplierCode, supplierRow.supplierName);
    setBidHistoryModal({
      supplierName: displayName,
      itemCode: item.itemCode || itemKey,
      bids: supBids,
    });
  };

  const isShowingInitialRankings =
    firstQuote &&
    firstQuoteBy === "Buyer" &&
    liveBidRanking.length === 0 &&
    fqInitialLeaderboard.length > 0;

  const onlineSet = React.useMemo(
    () => new Set(onlineSuppliers.map((s) => s.supplierCode)),
    [onlineSuppliers]
  );

  const myBids = isBuyer
    ? liveBids
    : liveBids.filter((b) => String(b.supplierCode) === String(currentUserCode));

  const myRankRow = !isBuyer
    ? effectiveLeaderboard.find((r) => String(r.supplierCode) === String(currentUserCode))
    : null;

  const canSupplierSeeRank = () => {
    if (displayRank === "notApplicable") return "hidden";
    if (displayRank === "onlyLeading") return myRankRow?.rank === 1 ? "visible" : "NA";
    return "visible";
  };
  // eslint-disable-next-line
  const rankVisibility = canSupplierSeeRank();

  const [activeTab, setActiveTab]           = useState(isBuyer ? "leaderboard" : "mybids");
  const [isCardVisible1, setCardVisible1]   = useState(true);
  const [isCardVisible2, setCardVisible2]   = useState(true);
  const [feedPage, setFeedPage]             = useState(1);
  const [supplierFilter, setSupplierFilter] = useState("all");
  // eslint-disable-next-line
  const [suppliersPage, setSuppliersPage]   = useState(1);

  const isEnded = auctionStatus === "ended";
  const auctionIsActuallyEnded =
    isEnded && (serverRemainingSeconds === null || serverRemainingSeconds <= 0);

  const statusLabel = auctionIsActuallyEnded ? "Ended"
    : auctionStatus === "extended" ? "Extended" : "Live";
  const statusColor = auctionIsActuallyEnded ? "#dc2626"
    : auctionStatus === "extended" ? "#b45309" : "#059669";
  const statusBg = auctionIsActuallyEnded ? "#fef2f2"
    : auctionStatus === "extended" ? "#fefce8" : "#f0fdf4";
  const statusBorder = auctionIsActuallyEnded ? "#fca5a5"
    : auctionStatus === "extended" ? "#fcd34d" : "#bbf7d0";

  const formatTime = (isoString) => {
    if (!isoString) return "--";
    try {
      return new Date(isoString).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
    } catch { return isoString; }
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === "") return "--";
    return Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  };

  //  getRankLabel — with emojis matching BidSubmitPanel 
  const getRankLabel = (rank) => {
    if (rank === 1) return { label: "🥇 L1", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" };
    if (rank === 2) return { label: "🥈 L2", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
    if (rank === 3) return { label: "🥉 L3", color: "#7c2d12", bg: "#fff3ed", border: "#fb923c" };
    return          { label: `🏅 L${rank}`, color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
  };

  const paginate = (data, page) => data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPagesCount = (data) => Math.max(1, Math.ceil(data.length / PAGE_SIZE));

  const Pagination = ({ page, setPage, data }) => {
    const total = totalPagesCount(data);
    if (total <= 1) return null;
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        gap: "4px", padding: "8px 14px",
        borderTop: "1px solid #e5e7eb", background: "#fafafa",
      }}>
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={pgBtnStyle(page === 1)}>&lsaquo;</button>
        {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
          <button key={p} onClick={() => setPage(p)} style={{
            width: "28px", height: "28px", borderRadius: "4px",
            border: p === page ? "1px solid #0f766e" : "1px solid #e5e7eb",
            background: p === page ? "#0f766e" : "#fff",
            color: p === page ? "#fff" : "#374151",
            fontWeight: 600, fontSize: "12px", cursor: "pointer",
          }}>{p}</button>
        ))}
        <button onClick={() => setPage((p) => Math.min(total, p + 1))} disabled={page === total} style={pgBtnStyle(page === total)}>&rsaquo;</button>
        <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "6px" }}>
          {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.length)} of {data.length}
        </span>
      </div>
    );
  };

  const tabs = isBuyer
    ? [
        { key: "leaderboard", label: "Leaderboard",  count: effectiveLeaderboard.length },
        { key: "itemranking", label: "Item Ranking",  count: effectiveItemLeaderboard.length },
        { key: "savings",     label: "Savings",       count: null },
        { key: "feed",        label: "Bid Feed",      count: liveBids.length },
      ]
    : [
        { key: "mybids",      label: "My Bids",       count: myBids.length },
        { key: "itemranking", label: "Item Ranking",   count: effectiveItemLeaderboard.length },
      ];

  const dtStyles = {
    rows: {
      style: { minHeight: "44px", fontSize: "12px", borderBottom: "1px solid #f1f5f9" },
    },
    headCells: {
      style: {
        fontWeight: 700, fontSize: "11px", textTransform: "uppercase",
        letterSpacing: "0.04em", color: "#6b7280", backgroundColor: "#f9fafb",
        borderBottom: "2px solid #e5e7eb", paddingTop: "10px", paddingBottom: "10px",
      },
    },
    cells: { style: { paddingTop: "8px", paddingBottom: "8px" } },
  };

  //  Leaderboard columns 
  const leaderColumns = [
    {
      name: "Rank",
      width: "100px",
      cell: (row) => {
        const rm = getRankLabel(row.rank);
        return (
          <span style={{
            background: rm.bg, color: rm.color,
            border: `1px solid ${rm.border}`,
            borderRadius: "6px", padding: "3px 10px",
            fontWeight: 700, fontSize: "12px", display: "inline-block",
          }}>
            {rm.label}
          </span>
        );
      },
    },
    {
      name: "Supplier",
      grow: 2,
      cell: (row) => {
        const isOnline = onlineSet.has(row.supplierCode);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{
              width: "7px", height: "7px", borderRadius: "50%", flexShrink: 0,
              background: isOnline ? "#10b981" : "#d1d5db", display: "inline-block",
            }} />
            <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "13px" }}>
              {getMaskedName(row.supplierCode, row.supplierName)}
            </span>
          </div>
        );
      },
    },
    {
      name: "Total Bid",
      right: true,
      cell: (row) => (
        <span style={{ fontWeight: 700, fontSize: "13px", color: row.rank === 1 ? "#059669" : "#374151" }}>
          {formatAmount(row.totalBid)}
        </span>
      ),
    },
    {
      name: "Last Bid At",
      right: true,
      cell: (row) => (
        row.isInitialPrice
          ? <span style={{ fontSize: "11px", color: "#6b7280", fontStyle: "italic" }}>Initial Price</span>
          : <span style={{ color: "#6b7280", fontSize: "12px" }}>{formatTime(row.lastBidTime)}</span>
      ),
    },
  ];

  //  My Bids columns 
  const myBidsColumns = [
    {
      name: "#",
      width: "60px",
      cell: (row, idx) => <span style={{ fontSize: "12px", color: "#6b7280" }}>{idx + 1}</span>,
    },
    {
      name: "Item",
      width: "160px",
      cell: (row) => (
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>
          {row.itemCode || row.itemKey || "--"}
        </span>
      ),
    },
    {
      name: "Bid Amount",
      right: true,
      cell: (row, idx) => (
        <span style={{ fontWeight: 700, fontSize: "13px", color: idx === 0 ? "#059669" : "#374151" }}>
          {formatAmount(row.totalBid)}
        </span>
      ),
    },
    {
      name: "Submitted At",
      right: true,
      cell: (row) => <span style={{ fontSize: "12px", color: "#6b7280" }}>{formatTime(row.bidTime)}</span>,
    },
  ];

  //  Invited Suppliers columns 
  const onlineCount  = invitedSuppliers.filter((s) => onlineSet.has(s.AssociateNumber)).length;
  const offlineCount = invitedSuppliers.length - onlineCount;

  const filteredSuppliers = invitedSuppliers.filter((s) => {
    if (supplierFilter === "online")  return onlineSet.has(s.AssociateNumber);
    if (supplierFilter === "offline") return !onlineSet.has(s.AssociateNumber);
    return true;
  });

  const supplierColumns = [
    {
      name: "Status",
      width: "100px",
      cell: (row) => {
        const isOnline = onlineSet.has(row.AssociateNumber);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{
              width: "8px", height: "8px", borderRadius: "50%",
              background: isOnline ? "#10b981" : "#d1d5db", display: "inline-block",
            }} />
            <span style={{ fontSize: "12px", fontWeight: 600, color: isOnline ? "#059669" : "#9ca3af" }}>
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>
        );
      },
    },
    {
      name: "Supplier ID",
      width: "130px",
      cell: (row) => <button className="btnInfo">{row.AssociateNumber || "--"}</button>,
    },
    {
      name: "Supplier Name",
      grow: 2,
      cell: (row) => <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "12px" }}>{row.organisationName || "--"}</span>,
    },
    {
      name: "Contact Person",
      cell: (row) => <span style={{ fontSize: "12px", color: "#374151" }}>{row.contactfirstName || "--"}</span>,
    },
    {
      name: "Email",
      grow: 2,
      cell: (row) => <span style={{ fontSize: "12px", color: "#374151" }}>{row.email || "--"}</span>,
    },
    {
      name: "Contact No",
      cell: (row) => <span style={{ fontSize: "12px", color: "#374151" }}>{row.contactNumber || "--"}</span>,
    },
  ];

  //  Item Rank columns 
  const buildItemRankColumns = (currentItem) => [
    {
      name: "Rank",
      width: "80px",
      cell: (r) => {
        const rm = getRankLabel(r.rank);
        return (
          <span style={{
            background: rm.bg, color: rm.color,
            border: `1px solid ${rm.border}`,
            borderRadius: "6px", padding: "3px 8px",
            fontWeight: 700, fontSize: "12px",
          }}>
            {rm.label}
          </span>
        );
      },
    },
    {
      name: "Supplier",
      grow: 2,
      cell: (r) => (
        <span style={{ fontWeight: r.rank === 1 ? 700 : 500, color: "#1e293b", fontSize: "12px" }}>
          {vendorMasking && auctionStatus !== "ended"
            ? getMaskedName(r.supplierCode, r.supplierName)
            : r.supplierName || r.supplierCode}
        </span>
      ),
    },
    {
      name: "Bid Amount",
      right: true,
      cell: (r) => (
        <span style={{ fontWeight: 700, fontSize: "13px", color: r.rank === 1 ? "#059669" : "#374151" }}>
          {typeof r.bidAmount === "number"
            ? r.bidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })
            : r.bidAmount}
        </span>
      ),
    },
    {
      name: "Time / Source",
      right: true,
      cell: (r) =>
        r.isInitialPrice
          ? <span style={{ fontSize: "11px", color: "#6b7280", fontStyle: "italic" }}>Initial Price</span>
          : <span style={{ color: "#6b7280", fontSize: "12px" }}>{formatTime(r.bidTime)}</span>,
    },
    ...(isBuyer ? [{
      name: "",
      width: "40px",
      cell: (r) => {
        const supBids = liveBids.filter(b => String(b.supplierCode) === String(r.supplierCode));
        if (!supBids.length) return null;
        return (
          <span
            onClick={() => openBidHistory(currentItem, r)}
            style={{ cursor: "pointer", fontSize: "18px", fontWeight: 700, color: "#0f766e", userSelect: "none" }}
            title="View bid history"
          >+</span>
        );
      },
    }] : []),
  ];

  const myInitialQuoteInLeaderboard = isShowingInitialRankings
    ? fqInitialLeaderboard.find((r) => String(r.supplierCode) === String(currentUserCode))
    : null;

  return (
    <>
      {/* ════ ACCORDION 1 — Live Auction Board ════ */}
      <div className="accordion mb-4" id="accordionLiveBidFeed">
        <div className="accordion-item" style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
          <h2 className="accordion-header">
            <Row className="accordion-button" style={{
              background: "light",
              margin: 0, cursor: "pointer",
            }}>
              <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                <i
                  className={`bi ${isCardVisible1 ? "bi-chevron-right" : "bi-chevron-down"}`}
                  onClick={() => setCardVisible1(!isCardVisible1)}
                />
              </Col>
              <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                <span className="iconLogo">{isBuyer ? "LA" : "MA"}</span>
              </Col>
              <Col xs={4} md={5} lg={5}>
                <span className="headerTitle" style={{ color: "#0f172a", fontWeight: 700 }}>
                  {isBuyer ? "Live Auction Board" : "My Auction Activity"}
                </span>
                <span className="subHeaderTitle" style={{ display: "flex", alignItems: "center", gap: "5px", color: "#1e293b" }}>
                  <span style={{
                    width: "7px", height: "7px", borderRadius: "50%",
                    background: isConnected ? "#10b981" : "#f87171",
                    display: "inline-block",
                    boxShadow: isConnected ? "0 0 6px #10b981" : "none",
                    animation: isConnected ? "pulse 1.5s infinite" : "none",
                  }} />
                  {isConnected ? "Connected" : "Disconnected"}
                </span>
              </Col>
              <Col xs={6} md={5} lg={5} className="headerButtonAlign"
                style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}
              >
                {isShowingInitialRankings && (
                  <span style={{
                    fontSize: "11px", color: "#6b7280",
                    border: "1px solid #e2e8f0", borderRadius: "4px",
                    padding: "2px 8px", fontWeight: 600,
                  }}>Initial Rankings</span>
                )}
                {isBuyer && vendorMasking && auctionStatus !== "ended" && (
                  <span style={{
                    fontSize: "11px", color: "#6b7280",
                    border: "1px solid #e2e8f0", borderRadius: "4px",
                    padding: "2px 8px", fontWeight: 600,
                  }}>Masked</span>
                )}
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: "5px",
                  background: statusBg, border: `1px solid ${statusBorder}`,
                  borderRadius: "4px", padding: "3px 10px",
                }}>
                  <span style={{
                    width: "7px", height: "7px", borderRadius: "50%",
                    background: statusColor, display: "inline-block",
                    animation: auctionStatus === "active" ? "pulse 1.5s infinite" : "none",
                  }} />
                  <span style={{ fontSize: "12px", fontWeight: 600, color: statusColor }}>{statusLabel}</span>
                </span>
              </Col>
            </Row>
          </h2>

          <div className={`accordion-collapse collapse show ${isCardVisible1 ? "visible" : ""}`}>
            <div className="accordion-body" style={{ padding: 0 }}>

              {isShowingInitialRankings && (
                <div style={{
                  padding: "8px 16px", background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0", fontSize: "11px", color: "#64748b",
                }}>
                  Showing initial rankings based on buyer-entered prices. Rankings will update once suppliers start bidding.
                </div>
              )}

              {/* Tabs */}
              <div style={{
                display: "flex", background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
                borderBottom: "1px solid #e2e8f0", padding: "0 4px",
              }}>
                {tabs.map((t) => (
                  <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                    flex: 1, padding: "12px 8px 10px", border: "none",
                    borderBottom: activeTab === t.key ? "3px solid #0f766e" : "3px solid transparent",
                    background: "transparent",
                    fontWeight: activeTab === t.key ? 700 : 500,
                    color: activeTab === t.key ? "#0f766e" : "#64748b",
                    cursor: "pointer", fontSize: "13px", transition: "all 0.2s ease",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                    letterSpacing: "0.02em",
                  }}>
                    <span>{t.label}</span>
                    {t.count !== null && (
                      <span style={{
                        background: activeTab === t.key ? "#ccfbf1" : "#f1f5f9",
                        color: activeTab === t.key ? "#0f766e" : "#94a3b8",
                        borderRadius: "10px", padding: "2px 8px",
                        fontSize: "10px", fontWeight: 700,
                        minWidth: "20px", textAlign: "center",
                      }}>{t.count}</span>
                    )}
                  </button>
                ))}
              </div>

              <div style={{ background: "#fff" }}>

                {/* BUYER: Leaderboard */}
                {isBuyer && activeTab === "leaderboard" && (
                  effectiveLeaderboard.length === 0 ? (
                    <EmptyState msg="Waiting for suppliers to bid." />
                  ) : (
                    <>
                      {isShowingInitialRankings && (
                        <div style={{
                          padding: "8px 16px", background: "#f8fafc",
                          borderBottom: "1px solid #e2e8f0", fontSize: "11px", color: "#64748b",
                        }}>
                          Buyer-entered initial prices are shown. Lowest total = L1. Live bids will replace these.
                        </div>
                      )}
                      <DataTable
                        columns={leaderColumns}
                        data={effectiveLeaderboard}
                        customStyles={dtStyles}
                        pagination
                        paginationPerPage={PAGE_SIZE}
                        highlightOnHover
                        conditionalRowStyles={[]}
                      />
                    </>
                  )
                )}

                {/* BUYER: Item Ranking */}
                {isBuyer && activeTab === "itemranking" && (
                  effectiveItemLeaderboard.length === 0 ? (
                    <EmptyState msg="No item bids yet. Rankings appear once suppliers start bidding." />
                  ) : (
                    <div style={{ padding: "12px" }}>
                      {isShowingInitialRankings && (
                        <div style={{
                          padding: "8px 12px", marginBottom: "10px",
                          background: "#f8fafc", border: "1px solid #e2e8f0",
                          borderRadius: "6px", fontSize: "11px", color: "#64748b",
                        }}>
                          Item-wise initial rankings based on buyer-entered prices. Will update once live bidding starts.
                        </div>
                      )}
                      {effectiveItemLeaderboard.map((item, iIdx) => (
                        <div key={iIdx} style={{
                          marginBottom: "12px", border: "1px solid #e2e8f0",
                          borderRadius: "6px", overflow: "hidden",
                        }}>
                          <div style={{
                            background: "#f9fafb",
                            borderBottom: "1px solid #e2e8f0",
                            padding: "10px 14px", display: "flex",
                            alignItems: "center", justifyContent: "space-between",
                          }}>
                            <div style={{
                              fontWeight: 700, fontSize: "14px", color: "#0f172a",
                              display: "flex", alignItems: "center", gap: "8px",
                            }}>
                              {item.lotNumber && (
                                <span style={{
                                  background: "#f3f4f6", color: "#374151",
                                  padding: "3px 10px", borderRadius: "4px",
                                  fontSize: "11px", fontWeight: 700,
                                }}>Lot {item.lotNumber}</span>
                              )}
                              {item.itemCode}
                              {item.itemDesc && (
                                <span style={{ fontWeight: 400, color: "#64748b", fontSize: "12px" }}>
                                  – {item.itemDesc}
                                </span>
                              )}
                            </div>
                            <span style={{
                              fontSize: "11px", color: "#6b7280", fontWeight: 500,
                            }}>
                              {item.rankings.length} {item.rankings[0]?.isInitialPrice ? "initial price" : "bidder"}
                              {item.rankings.length !== 1 ? "s" : ""}
                            </span>
                          </div>

                          <DataTable
                            columns={buildItemRankColumns(item)}
                            data={item.rankings}
                            customStyles={dtStyles}
                            highlightOnHover
                            conditionalRowStyles={[]}
                          />
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* BUYER: Savings */}
                {isBuyer && activeTab === "savings" && (
                  effectiveItemLeaderboard.length === 0 ? (
                    <EmptyState msg="No item data yet. Savings appear once items and bids are available." />
                  ) : (
                    <div style={{ padding: "12px" }}>
                      {effectiveItemLeaderboard.map((item, iIdx) => {
                        const sp = parseFloat(itemPrices?.[item.local_id]) || 0;
                        const rp = parseFloat(itemReservePrice?.[item.local_id]) || 0;
                        const l1 = item.rankings?.length
                          ? Math.min(...item.rankings.map(r => parseFloat(r.bidAmount) || 0))
                          : null;
                        const spSaving = (sp && l1 != null && l1 > 0) ? sp - l1 : null;
                        const rpSaving = (rp && l1 != null && l1 > 0) ? rp - l1 : null;
                        const spPct = (spSaving != null && sp) ? (spSaving / sp) * 100 : null;
                        const rpPct = (rpSaving != null && rp) ? (rpSaving / rp) * 100 : null;
                        const spColor = (spSaving != null && spSaving > 0) ? "#059669" : (spSaving != null ? "#dc2626" : "#9ca3af");
                        const rpColor = (rpSaving != null && rpSaving > 0) ? "#059669" : (rpSaving != null ? "#dc2626" : "#9ca3af");
                        const hasAny = sp || rp;
                        if (!hasAny) return null;
                        return (
                          <div key={iIdx} style={{
                            marginBottom: "12px", border: "1px solid #e2e8f0",
                            borderRadius: "8px", overflow: "hidden",
                          }}>
                            <div style={{
                              background: "#f9fafb",
                              padding: "10px 14px", display: "flex", alignItems: "center", gap: "8px",
                              borderBottom: "1px solid #e2e8f0",
                            }}>
                              <span style={{ fontSize: "14px" }}>📦</span>
                              <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                                {item.itemCode || `Item ${iIdx + 1}`}
                              </span>
                              {item.itemDesc && (
                                <span style={{ fontWeight: 400, color: "#64748b", fontSize: "12px" }}>
                                  – {item.itemDesc}
                                </span>
                              )}
                            </div>
                            <div style={{
                              display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr",
                              gap: "1px", background: "#e2e8f0",
                            }}>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>Start Price</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>{sp ? formatAmount(sp) : "—"}</div>
                              </div>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>Reserve Price</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>{rp ? formatAmount(rp) : "—"}</div>
                              </div>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>SP Savings</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: spColor }}>
                                  {spSaving != null ? formatAmount(spSaving) : "—"}
                                </div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: spColor, marginTop: "3px" }}>
                                  {spPct != null ? `${spPct.toFixed(1)}%` : ""}
                                </div>
                              </div>
                              <div style={{ background: "#fff", padding: "14px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>RP Savings</div>
                                <div style={{ fontSize: "16px", fontWeight: 800, color: rpColor }}>
                                  {rpSaving != null ? formatAmount(rpSaving) : "—"}
                                </div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: rpColor, marginTop: "3px" }}>
                                  {rpPct != null ? `${rpPct.toFixed(1)}%` : ""}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {(startPrice || Object.keys(itemReservePrice).length > 0) && (() => {
                        const totalSp = Object.values(itemPrices).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                        const totalRp = Object.values(itemReservePrice).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                        const l1Total = effectiveLeaderboard[0]?.totalBid || 0;
                        const spTotalSaving = totalSp && l1Total ? totalSp - l1Total : null;
                        const rpTotalSaving = totalRp && l1Total ? totalRp - l1Total : null;
                        const spTotalPct = spTotalSaving != null && totalSp ? (spTotalSaving / totalSp) * 100 : null;
                        const rpTotalPct = rpTotalSaving != null && totalRp ? (rpTotalSaving / totalRp) * 100 : null;
                        if (!totalSp && !totalRp) return null;
                        return (
                          <div style={{
                            marginTop: "16px", border: "1px solid #d1d5db",
                            borderRadius: "8px", overflow: "hidden",
                          }}>
                            <div style={{
                              background: "#f3f4f6",
                              padding: "10px 14px", borderBottom: "1px solid #e5e7eb",
                            }}>
                              <span style={{ fontWeight: 700, fontSize: "13px", color: "#374151" }}>
                                📊 Overall Totals
                              </span>
                            </div>
                            <div style={{
                              display: "grid", gridTemplateColumns: totalSp && totalRp ? "1fr 1fr 1fr 1fr" : "1fr 1fr",
                              gap: "1px", background: "#e5e7eb",
                            }}>
                              {totalSp > 0 && (
                                <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                  <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Total Start Price</div>
                                  <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>{formatAmount(totalSp)}</div>
                                </div>
                              )}
                              {totalRp > 0 && (
                                <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                  <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Total Reserve Price</div>
                                  <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>{formatAmount(totalRp)}</div>
                                </div>
                              )}
                              {totalSp > 0 && (
                                <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                  <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                                    Total SP Savings
                                  </div>
                                  <div style={{ fontSize: "15px", fontWeight: 800, color: spTotalSaving != null ? (spTotalSaving > 0 ? "#059669" : "#dc2626") : "#9ca3af" }}>
                                    {spTotalSaving != null ? formatAmount(spTotalSaving) : "—"}
                                  </div>
                                  <div style={{ fontSize: "11px", fontWeight: 600, color: spTotalSaving != null ? (spTotalSaving > 0 ? "#059669" : "#dc2626") : "#9ca3af", marginTop: "2px" }}>
                                    {spTotalPct != null ? `${spTotalPct.toFixed(1)}%` : ""}
                                  </div>
                                </div>
                              )}
                              {totalRp > 0 && (
                                <div style={{ background: "#fff", padding: "12px", textAlign: "center" }}>
                                  <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                                    Total RP Savings
                                  </div>
                                  <div style={{ fontSize: "15px", fontWeight: 800, color: rpTotalSaving != null ? (rpTotalSaving > 0 ? "#059669" : "#dc2626") : "#9ca3af" }}>
                                    {rpTotalSaving != null ? formatAmount(rpTotalSaving) : "—"}
                                  </div>
                                  <div style={{ fontSize: "11px", fontWeight: 600, color: rpTotalSaving != null ? (rpTotalSaving > 0 ? "#059669" : "#dc2626") : "#9ca3af", marginTop: "2px" }}>
                                    {rpTotalPct != null ? `${rpTotalPct.toFixed(1)}%` : ""}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )
                )}

                {/* BUYER: Bid Feed */}
                {isBuyer && activeTab === "feed" && (
                  liveBids.length === 0 ? (
                    <EmptyState msg="No bids received yet." />
                  ) : (
                    <>
                      <div>
                        {paginate(liveBids, feedPage).map((bid, idx) => {
                          const globalIdx = (feedPage - 1) * PAGE_SIZE + idx;
                          const displayName = getMaskedName(bid.supplierCode, bid.supplierName);
                          return (
                            <div key={idx} style={{
                              padding: "10px 16px", borderBottom: "1px solid #f1f5f9",
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                              background: globalIdx === 0 ? "#f0fdf4" : "#fff",
                              borderLeft: globalIdx === 0 ? "3px solid #10b981" : "3px solid transparent",
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <div style={{
                                  width: "30px", height: "30px", borderRadius: "50%",
                                  background: "#e2e8f0", display: "flex",
                                  alignItems: "center", justifyContent: "center",
                                  fontSize: "12px", fontWeight: 700, color: "#475569", flexShrink: 0,
                                }}>
                                  {displayName[0]?.toUpperCase() || "?"}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: "13px", color: "#1e293b" }}>{displayName}</div>
                                  <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "1px" }}>{formatTime(bid.bidTime)}</div>
                                </div>
                              </div>
                              <div style={{ fontWeight: 700, fontSize: "13px", color: globalIdx === 0 ? "#059669" : "#374151" }}>
                                {formatAmount(bid.totalBid)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Pagination page={feedPage} setPage={setFeedPage} data={liveBids} />
                    </>
                  )
                )}

                {/* SUPPLIER: My Bids */}
                {!isBuyer && activeTab === "mybids" && (
                  <>
                    {firstQuote && firstQuoteBy === "Buyer" && myInitialQuoteInLeaderboard && myBids.length === 0 && (
                      <div style={{
                        margin: "12px", padding: "12px 16px",
                        background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px",
                      }}>
                        <div style={{
                          fontSize: "11px", color: "#6b7280", fontWeight: 600,
                          marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.04em",
                        }}>Buyer-Set Initial Price</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {(() => {
                              const rm = getRankLabel(myInitialQuoteInLeaderboard.rank);
                              return (
                                <span style={{
                                  background: rm.bg, color: rm.color,
                                  border: `1px solid ${rm.border}`,
                                  borderRadius: "6px", padding: "3px 10px",
                                  fontWeight: 700, fontSize: "12px",
                                }}>{rm.label}</span>
                              );
                            })()}
                            <span style={{ fontSize: "12px", color: "#374151" }}>
                              Your initial rank (based on buyer-entered price)
                            </span>
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                            {formatAmount(myInitialQuoteInLeaderboard.totalBid)}
                          </span>
                        </div>
                        <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "6px" }}>
                          Start bidding to improve your rank.
                        </div>
                      </div>
                    )}
                    {myBids.length === 0 && !(firstQuote && firstQuoteBy === "Buyer" && myInitialQuoteInLeaderboard)
                      ? <EmptyState msg="You haven't placed any bids yet." />
                      : myBids.length > 0
                        ? (
                          <DataTable
                            columns={myBidsColumns}
                            data={myBids}
                            customStyles={dtStyles}
                            pagination
                            paginationPerPage={PAGE_SIZE}
                            highlightOnHover
                            conditionalRowStyles={[{
                              when: (_, idx) => idx === 0,
                              style: { backgroundColor: "#f0fdf4", borderLeft: "3px solid #10b981" },
                            }]}
                          />
                        ) : null}
                  </>
                )}

                {/* SUPPLIER: Item Ranking */}
                {!isBuyer && activeTab === "itemranking" && (
                  effectiveItemLeaderboard.length === 0 ? (
                    <EmptyState msg="No item bids yet. Rankings appear once bidding begins." />
                  ) : (
                    <div style={{ padding: "12px" }}>
                      {firstQuote && firstQuoteBy === "Buyer" && isShowingInitialRankings && (
                        <div style={{
                          padding: "8px 12px", marginBottom: "10px",
                          background: "#f8fafc", border: "1px solid #e2e8f0",
                          borderRadius: "6px", fontSize: "11px", color: "#64748b",
                        }}>
                          Initial item rankings based on buyer-set prices. Your rank will update as bidding proceeds.
                        </div>
                      )}
                      {effectiveItemLeaderboard.map((item, iIdx) => {
                        const myItemRank = item.rankings?.find(
                          (r) => String(r.supplierCode) === String(currentUserCode)
                        );
                        const isItemEnded = auctionStatus === "ended";
                        const isInitialItem = item.rankings?.[0]?.isInitialPrice;
                        return (
                          <div key={iIdx} style={{
                            marginBottom: "12px", border: "1px solid #e2e8f0",
                            borderRadius: "6px", overflow: "hidden", background: "#fff",
                          }}>
                            <div style={{
                              background: "#f9fafb", borderBottom: "1px solid #e2e8f0",
                              padding: "10px 16px", display: "flex",
                              alignItems: "center", justifyContent: "space-between",
                            }}>
                              <div style={{
                                fontWeight: 700, fontSize: "13px", color: "#1e293b",
                                display: "flex", alignItems: "center", gap: "8px",
                              }}>
                                {item.lotNumber && (
                                  <span style={{
                                    background: "#e0f2fe", color: "#0369a1",
                                    padding: "2px 8px", borderRadius: "4px",
                                    fontSize: "11px", fontWeight: 600,
                                  }}>Lot {item.lotNumber}</span>
                                )}
                                {item.itemCode}
                                {item.itemDesc && (
                                  <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px" }}>
                                    {item.itemDesc}
                                  </span>
                                )}
                                {isInitialItem && (
                                  <span style={{ fontSize: "11px", color: "#6b7280", fontStyle: "italic" }}>(Initial)</span>
                                )}
                              </div>
                              {myItemRank && (() => {
                                const rm = getRankLabel(myItemRank.rank);
                                return (
                                  <span style={{
                                    fontSize: "12px", fontWeight: 700,
                                    padding: "3px 10px", borderRadius: "6px",
                                    background: rm.bg, color: rm.color,
                                    border: `1px solid ${rm.border}`,
                                  }}>{myItemRank.rank === 1 ? "You: 🥇 L1" : `You: ${rm.label}`}</span>
                                );
                              })()}
                            </div>

                            <div style={{ padding: "12px 16px", background: "#fff" }}>
                              {myItemRank ? (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    {(() => {
                                      const rm = getRankLabel(myItemRank.rank);
                                      return (
                                        <span style={{
                                          background: rm.bg, color: rm.color,
                                          border: `1px solid ${rm.border}`,
                                          borderRadius: "6px", padding: "3px 10px",
                                          fontSize: "12px", fontWeight: 700,
                                        }}>{rm.label}</span>
                                      );
                                    })()}
                                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
                                      {myItemRank.isInitialPrice ? "Your Initial Price (Buyer Set)"
                                        : isItemEnded ? "Your Final Bid" : "Your Bid"}
                                    </span>
                                  </div>
                                  <span style={{
                                    fontSize: "14px", fontWeight: 700,
                                    color: myItemRank.rank === 1 ? "#059669" : "#1e293b",
                                  }}>
                                    {typeof myItemRank.bidAmount === "number"
                                      ? myItemRank.bidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                                      : myItemRank.bidAmount}
                                  </span>
                                </div>
                              ) : (
                                <div style={{ textAlign: "center", padding: "4px 0", fontSize: "12px", color: "#9ca3af" }}>
                                  {isItemEnded ? "No bid placed for this item." : "You haven't placed a bid for this item yet."}
                                </div>
                              )}
                            </div>

                            {isItemEnded ? (
                              <div style={{
                                padding: "6px 16px", background: "#f8fafc",
                                borderTop: "1px solid #e2e8f0", fontSize: "11px",
                                color: "#64748b", textAlign: "center",
                              }}>Auction has ended. Bidding is no longer available.</div>
                            ) : myItemRank?.rank === 1 ? (
                              <div style={{
                                padding: "6px 16px", background: "#f0fdf4",
                                borderTop: "1px solid #bbf7d0", fontSize: "11px",
                                color: "#166534", textAlign: "center", fontWeight: 600,
                              }}>
                                {myItemRank.isInitialPrice
                                  ? "You have the best initial price for this item."
                                  : "🎉 You are the L1 bidder for this item!"}
                              </div>
                            ) : myItemRank?.isInitialPrice ? (
                              <div style={{
                                padding: "6px 16px", background: "#f8fafc",
                                borderTop: "1px solid #e2e8f0", fontSize: "11px",
                                color: "#64748b", textAlign: "center",
                              }}>Bid lower to improve your rank for this item.</div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

              </div>

              {/* Footer (buyer only) */}
              {isBuyer && (
                <div style={{
                  borderTop: "1px solid #e2e8f0",
                  background: "#f9fafb",
                  padding: "10px 16px", display: "flex",
                  gap: "16px", fontSize: "12px", color: "#6b7280", flexWrap: "wrap",
                  alignItems: "center",
                }}>
                  <span>Total Bids: <strong style={{ color: "#374151" }}>{liveBids.length}</strong></span>
                  <span>Suppliers: <strong style={{ color: "#374151" }}>{effectiveLeaderboard.length}</strong></span>
                  {effectiveLeaderboard[0] && (
                    <span>L1 Amount: <strong style={{ color: "#059669" }}>{formatAmount(effectiveLeaderboard[0].totalBid)}</strong></span>
                  )}
                  {(startPrice || Object.keys(itemReservePrice).length > 0) && (() => {
                    const totalSp = Object.values(itemPrices).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                    const totalRp = Object.values(itemReservePrice).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                    const l1Total = effectiveLeaderboard[0]?.totalBid || 0;
                    if ((!totalSp && !totalRp) || !l1Total) return null;
                    return (
                      <>
                        {totalSp > 0 && (() => {
                          const s = totalSp - l1Total;
                          const p = (s / totalSp) * 100;
                          const c = p > 0 ? "#059669" : "#dc2626";
                          return <span>SP Savings: <strong style={{ color: c }}>{s.toFixed(2)} ({p.toFixed(1)}%)</strong></span>;
                        })()}
                        {totalRp > 0 && (() => {
                          const s = totalRp - l1Total;
                          const p = (s / totalRp) * 100;
                          const c = p > 0 ? "#059669" : "#dc2626";
                          return <span>RP Savings: <strong style={{ color: c }}>{s.toFixed(2)} ({p.toFixed(1)}%)</strong></span>;
                        })()}
                      </>
                    );
                  })()}
                  <span>Online: <strong style={{ color: "#374151" }}>{onlineCount}</strong></span>
                  <span>Offline: <strong style={{ color: "#374151" }}>{offlineCount}</strong></span>
                  {isShowingInitialRankings && (
                    <span style={{ color: "#6b7280", fontStyle: "italic" }}>Showing initial rankings — live bids will replace these</span>
                  )}
                  {vendorMasking && auctionStatus !== "ended" && (
                    <span style={{ color: "#6b7280", fontWeight: 600 }}>Supplier names masked until auction closes</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ════ Bid History Modal ════ */}
      {bidHistoryModal && (
        <div
          onClick={() => setBidHistoryModal(null)}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.4)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "12px", width: "450px", maxWidth: "90vw",
              maxHeight: "80vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "16px 20px", borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
              borderRadius: "12px 12px 0 0",
            }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: "15px", color: "#0f172a" }}>📋 {bidHistoryModal.supplierName}</span>
                <span style={{ fontSize: "12px", color: "#64748b", marginLeft: "8px" }}>— {bidHistoryModal.itemCode}</span>
              </div>
              <span
                onClick={() => setBidHistoryModal(null)}
                style={{ cursor: "pointer", fontSize: "20px", color: "#64748b", fontWeight: 700, lineHeight: 1 }}
              >×</span>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {bidHistoryModal.bids.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "#9ca3af", fontSize: "13px" }}>
                  No bid history found.
                </div>
              ) : (
                <div>
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    padding: "8px 12px", fontSize: "11px", fontWeight: 700, color: "#64748b",
                    textTransform: "uppercase", letterSpacing: "0.04em",
                    borderBottom: "2px solid #e2e8f0", marginBottom: "8px",
                  }}>
                    <span>Time</span>
                    <span>Bid Amount</span>
                  </div>
                  {bidHistoryModal.bids.map((b, idx) => (
                    <div key={idx} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "9px 12px", marginBottom: "4px",
                      background: idx === 0 ? "#f0fdf4" : "#fff",
                      border: "1px solid #f1f5f9", borderRadius: "6px",
                    }}>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>{formatTime(b.bidTime)}</span>
                      <span style={{ fontWeight: 700, fontSize: "13px", color: idx === 0 ? "#059669" : "#374151" }}>
                        {formatAmount(b.totalBid)}
                      </span>
                    </div>
                  ))}
                  <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "10px", textAlign: "center" }}>
                    {bidHistoryModal.bids.length} bid{bidHistoryModal.bids.length !== 1 ? "s" : ""} placed
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/*  ACCORDION 2 — Invited Suppliers (buyer only)  */}
      {isBuyer && (
        <div className="accordion mb-4" id="accordionInvitedSuppliers">
          <div className="accordion-item" style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
            <h2 className="accordion-header">
              <Row className="accordion-button" style={{ background: "#fff", margin: 0 }}>
                <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                  <i
                    className={`bi ${isCardVisible2 ? "bi-chevron-right" : "bi-chevron-down"}`}
                    onClick={() => setCardVisible2(!isCardVisible2)}
                  />
                </Col>
                <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                  <span className="iconLogo">SL</span>
                </Col>
                <Col xs={4} md={5} lg={5}>
                  <span className="headerTitle">Invited Suppliers</span>
                  <span className="subHeaderTitle">Online / Offline Status</span>
                </Col>
                <Col xs={6} md={5} lg={5} className="headerButtonAlign"
                  style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end", flexWrap: "wrap" }}
                >
                  {[
                    { val: "all",     label: `All (${invitedSuppliers.length})` },
                    { val: "online",  label: `Online (${onlineCount})` },
                    { val: "offline", label: `Offline (${offlineCount})` },
                  ].map((f) => (
                    <span key={f.val}
                      onClick={() => { setSupplierFilter(f.val); setSuppliersPage(1); }}
                      style={{
                        padding: "3px 10px", borderRadius: "4px", cursor: "pointer",
                        fontSize: "12px", fontWeight: 600,
                        border: supplierFilter === f.val ? "1px solid #0f766e" : "1px solid #e2e8f0",
                        background: supplierFilter === f.val ? "#f0fdf4" : "#fff",
                        color: supplierFilter === f.val ? "#0f766e" : "#6b7280",
                        transition: "all 0.15s",
                      }}
                    >{f.label}</span>
                  ))}
                </Col>
              </Row>
            </h2>
            <div className={`accordion-collapse collapse show ${isCardVisible2 ? "visible" : ""}`}>
              <div className="accordion-body" style={{ padding: "10px" }}>
                {filteredSuppliers.length === 0 ? (
                  <EmptyState msg="No suppliers found." />
                ) : (
                  <DataTable
                    columns={supplierColumns}
                    data={filteredSuppliers}
                    customStyles={dtStyles}
                    pagination
                    paginationPerPage={10}
                    highlightOnHover
                    conditionalRowStyles={[{
                      when: (row) => onlineSet.has(row.AssociateNumber),
                      style: { backgroundColor: "#f0fdf4", borderLeft: "3px solid #10b981" },
                    }]}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>
    </>
  );
};

const pgBtnStyle = (disabled) => ({
  width: "28px", height: "28px", borderRadius: "4px",
  border: "1px solid #e2e8f0",
  background: disabled ? "#f9fafb" : "#fff",
  color: disabled ? "#d1d5db" : "#374151",
  fontWeight: 700, fontSize: "14px",
  cursor: disabled ? "not-allowed" : "pointer",
});

const EmptyState = ({ msg }) => (
  <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af", fontSize: "13px" }}>
    {msg}
  </div>
);

export default LiveBidFeed;